/**
 * Physics simulation for free fall with air resistance
 */

import type {
  Ball,
  SimulationState,
  SimulationParams,
  TrailMarker,
  HistoryPoint,
  Simulation,
  PhysicsConstants
} from '../types';

// Physics constants
const CONSTANTS: PhysicsConstants = {
  TOWER_HEIGHT: 53,      // meters (actual Pisa Tower height)
  DRAG_COEFFICIENT: 0.47 // sphere drag coefficient
};

/**
 * Create a ball with computed properties from mass and density
 */
function createBall(
  id: 'blue' | 'red',
  mass: number,
  density: number,
  color: string
): Ball {
  // Calculate volume from mass and density: V = m / ρ
  const volume = mass / density;

  // Calculate radius from volume: V = (4/3) * π * r³ → r = (3V / 4π)^(1/3)
  const radius = Math.pow((3 * volume) / (4 * Math.PI), 1/3);

  const diameter = 2 * radius;
  const crossSectionArea = Math.PI * radius * radius;

  return {
    id,
    diameter,
    density,
    mass,
    radius,
    crossSectionArea,
    position: 5, // starts at top
    velocity: 0, // starts at rest
    color,
    hasLanded: false
  };
}

/**
 * Calculate acceleration due to gravity and air drag
 * Using: a = g - (0.5 * C_d * ρ_air * A * v²) / m
 */
function calculateAcceleration(
  ball: Ball,
  gravity: number,
  airDensity: number
): number {
  const dragForce =
    0.5 *
    CONSTANTS.DRAG_COEFFICIENT *
    airDensity *
    ball.crossSectionArea *
    ball.velocity *
    ball.velocity;

  const dragAcceleration = dragForce / ball.mass;

  // Gravity acts downward (positive), drag opposes motion (negative)
  return gravity - dragAcceleration;
}

/**
 * Update ball state using Euler integration
 */
function updateBall(
  ball: Ball,
  dt: number,
  gravity: number,
  airDensity: number
): void {
  if (ball.hasLanded) return;

  // Calculate acceleration
  const acceleration = calculateAcceleration(ball, gravity, airDensity);

  // Update velocity: v = v + a*dt
  ball.velocity += acceleration * dt;

  // Update position: s = s + v*dt
  ball.position += ball.velocity * dt;

  // Check if ball has reached the ground
  if (ball.position >= CONSTANTS.TOWER_HEIGHT) {
    ball.position = CONSTANTS.TOWER_HEIGHT - 0.5;  // slightly above ground
    ball.velocity = 0;
    ball.hasLanded = true;
  }
}

/**
 * Update trail markers
 */
function updateTrail(
  ball: Ball,
  trail: TrailMarker[],
  currentTime: number,
  lastTrailTime: { blue: number; red: number },
  trailInterval: number
): void {
  if (ball.hasLanded) return;

  const timeSinceLastMarker = currentTime - lastTrailTime[ball.id];

  if (timeSinceLastMarker >= trailInterval) {
    trail.push({
      position: ball.position,
      time: currentTime
    });
    lastTrailTime[ball.id] = currentTime;
  }
}

/**
 * Update history for graphs
 */
function updateHistory(
  ball: Ball,
  history: HistoryPoint[],
  currentTime: number
): void {
  history.push({
    time: currentTime,
    position: ball.position,
    velocity: ball.velocity
  });

  // Keep only last 1000 points to avoid memory issues
  if (history.length > 1000) {
    history.shift();
  }
}

/**
 * Main simulation class
 */
export class PhysicsSimulation implements Simulation {
  state: SimulationState;
  private params: SimulationParams;

  constructor(params: SimulationParams) {
    this.params = params;
    this.state = this.createInitialState(params);
  }

  private createInitialState(params: SimulationParams): SimulationState {
    return {
      time: 0,
      running: false,
      blueBall: createBall('blue', params.blueMass, params.blueDensity, '#3b82f6'),
      redBall: createBall('red', params.redMass, params.redDensity, '#ef4444'),
      blueTrail: [],
      redTrail: [],
      blueHistory: [],
      redHistory: [],
      trailInterval: 0.2, // marker every 0.2 seconds
      lastTrailTime: { blue: 0, red: 0 },
      airDensity: params.airDensity
    };
  }

  play(): void {
    this.state.running = true;
  }

  pause(): void {
    this.state.running = false;
  }

  isComplete(): boolean {
    return this.state.blueBall.hasLanded && this.state.redBall.hasLanded;
  }

  step(dt: number): void {
    if (!this.state.running || this.isComplete()) return;

    // Apply time scale
    const scaledDt = dt * this.params.timeScale;

    // Update time
    this.state.time += scaledDt;

    // Update both balls
    updateBall(
      this.state.blueBall,
      scaledDt,
      this.params.gravity,
      this.params.airDensity
    );
    updateBall(
      this.state.redBall,
      scaledDt,
      this.params.gravity,
      this.params.airDensity
    );

    // Update trails
    updateTrail(
      this.state.blueBall,
      this.state.blueTrail,
      this.state.time,
      this.state.lastTrailTime,
      this.state.trailInterval
    );
    updateTrail(
      this.state.redBall,
      this.state.redTrail,
      this.state.time,
      this.state.lastTrailTime,
      this.state.trailInterval
    );

    // Update history for graphs
    updateHistory(this.state.blueBall, this.state.blueHistory, this.state.time);
    updateHistory(this.state.redBall, this.state.redHistory, this.state.time);

    // Stop simulation when both balls have landed
    if (this.isComplete()) {
      this.state.running = false;
    }
  }

  reset(params: SimulationParams): void {
    this.params = params;
    this.state = this.createInitialState(params);
  }

  updateParams(params: SimulationParams): void {
    this.params = params;
    // Update airDensity in state when params change
    this.state.airDensity = params.airDensity;
  }
}

export { CONSTANTS };
